<template>
  <div>
    <div class="calculator-container">
      <h1 class="h3">
        Difference in Means Calculator
      </h1>
      <span class="your-data">
        Your Samples are:
      </span>
      <Dropdown
        class="calculation-method samples-type"
        calculator
        :selected-title="selectedSampleType"
        :selected="selectedSampleType"
        :radio-buttons="{ Dependent: 'Dependent', Independent: 'Independent' }"
        @change="currentSelectedSampleType"
      />
      <div class="alternative-expected">
        <div class="alternative">
          <div class="label-tooltip desktop">
            <label>
              Alternative Hypothesis (<span class="alternative-symbols">H<sub>a</sub></span>)
            </label>
            <div class="icon-info-tooltip">
              <IconInfo />
              <Tooltip>
                <span>The hypothesis test’s direction based on the expected difference between the two samples’ means ({{ currentRawDataLabels[0] }} minus {{ currentRawDataLabels[1] }}).</span>
              </Tooltip>
            </div>
          </div>
          <div class="label-tooltip mobile">
            <label>
              Alternative Hypothesis
            </label>
            <div class="symbol-icon-tooltip">
              (<span class="alternative-symbols">H<sub>a</sub></span>)
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Provide a numerical value.</span>
                </Tooltip>
              </div>
            </div>
          </div>
          <Dropdown
            calculator
            html-radio
            :selected-title="selectedAltHypothesis"
            :selected="selectedAltHypothesis"
            :radio-buttons="{ '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>': '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>', '<span>µ<sub>d</sub>>µ<sub>0<sub></span>': '<span>µ<sub>d</sub>>µ<sub>0<sub></span>', '<span>µ<sub>d</sub><µ<sub>0<sub></span>': '<span>µ<sub>d</sub><µ<sub>0<sub></span>' }"
            @change="currentSelectedAltHypothesis"
          />
          <div v-if="altHypothesisAlert" class="error">
            {{ altHypothesisAlert }}
          </div>
        </div>
        <div class="expected">
          <div class="label-tooltip">
            <label>
              Expected Difference
            </label>
            <div class="icon-info-tooltip">
              <IconInfo />
              <Tooltip>
                <span v-if="selectedSampleType === 'Dependent'">The expected difference between the two sample’s means (After minus Before). Typically set to zero.</span>
                <span v-else>The expected difference between the two independent sample’s means. Typically set to zero.</span>
              </Tooltip>
            </div>
          </div>
          <input
            v-model="expectedDifference" type="text"
            :class="{ 'invalid-value': expectedDifferenceAlert }"
          >
          <div v-if="expectedDifferenceAlert" class="error">
            {{ expectedDifferenceAlert }}
          </div>
        </div>
      </div>
      <span class="your-data">
        Your Data is:
      </span>
      <Dropdown
        class="calculation-method"
        calculator
        :selected-title="selectedCalculationMethod"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ readyData: firstMethodName, rawData: 'Raw Data' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div class="standard-deviation">
          <div
            class="question-dropdown" :class="{
              'raw-data-version': hidePopulationSampleInput
                || (selectedSampleType === 'Independent' && selectedAnswer === 'Yes'),
              'remove-calc': selectedSampleType === 'Independent' && selectedAnswer === 'No' && selectedCalculationMethod === 'rawData'
            }"
          >
            <label>
              Do you know the population standard deviation (σ)?
            </label>
            <Dropdown
              calculator
              class="small-dropdown"
              :selected-title="selectedAnswer"
              :selected="selectedAnswer"
              :radio-buttons="{ Yes: 'Yes', No: 'No' }"
              @change="currentSelectedAnswer"
            />
          </div>
          <div v-if="!hidePopulationSampleInput && selectedSampleType === 'Dependent'" class="population-sample">
            <div class="label-tooltip">
              <label>
                {{ populationOrSample[0] }} <span v-if="selectedAnswer === 'No'" class="alternative-symbols">(s<sub>d</sub>)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                  <span>The standard deviation of the paired differences between the samples (After minus Before).</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="populationSampleData[populationOrSample[1]]" type="text"
              :class="{ 'invalid-value': populationSampleData[populationOrSample[2]] }"
            >
            <div
              v-if="populationSampleData[populationOrSample[2]] && !hidePopulationSampleInput"
              class="error"
            >
              {{ populationSampleData[populationOrSample[2]] }}
            </div>
          </div>
          <div v-else-if="selectedSampleType === 'Independent' && selectedAnswer === 'No'" class="sample-assumptions">
            <div class="label-tooltip">
              <label>
                Samples are assumed to be:
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Determine whether the samples are expected to have similar or different levels of variability.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              class="small-dropdown"
              :selected-title="selectedSampleAssumption"
              :selected="selectedSampleAssumption"
              :radio-buttons="{ 'Equal': 'Equal', 'Unequal': 'Unequal' }"
              @change="currentSelectedSampleAssumption"
            />
          </div>
        </div>

        <!-- Dependent version -->
        <div v-if="selectedCalculationMethod === 'readyData' && selectedSampleType === 'Dependent'" class="sample-inputs">
          <div>
            <div class="label-tooltip">
              <label class="sample-mean-label">
                Sample Mean <span>(x̄<sub>d</sub>)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>The average of the differences between the paired observations (After minus Before).</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="meanDifference" type="text"
              :class="{ 'invalid-value': meanDifferenceAlert }"
            >
            <div v-if="meanDifferenceAlert" class="error">
              {{ meanDifferenceAlert }}
            </div>
          </div>
          <div class="sample-size">
            <div class="label-tooltip">
              <label>
                Sample Size (n)
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Number of paired observations.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="size" type="text"
              :class="{ 'invalid-value': sizeAlert }"
            >
            <div v-if="sizeAlert" class="error">
              {{ sizeAlert }}
            </div>
          </div>
        </div>

        <!-- Independent version -->
        <div v-if="!hidePopulationSampleInput && selectedSampleType === 'Independent'" class="two-standard-deviations">
          <div class="population-sample">
            <div class="label-tooltip desktop">
              <label v-html="twoPopulationOrSample(1)[0]" />
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                  <span>The first sample’s standard deviation. Provide a numerical and positive value.</span>
                </Tooltip>
              </div>
            </div>
            <div class="label-tooltip mobile">
              <label v-html="twoPopulationOrSample(1, true)[0][0]" />
              <div class="second-row">
                <label v-html="twoPopulationOrSample(1, true)[0][1]" />
                <div class="icon-info-tooltip">
                  <IconInfo />
                  <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                    <span>The first sample’s standard deviation. Provide a numerical and positive value.</span>
                  </Tooltip>
                </div>
              </div>
            </div>
            <input
              v-model="populationSampleDataIndependent[twoPopulationOrSample(1)[1]]" type="text"
              :class="{ 'invalid-value': populationSampleDataIndependent[twoPopulationOrSample(1)[2]] }"
            >
            <div
              v-if="populationSampleDataIndependent[twoPopulationOrSample(1)[2]] && !hidePopulationSampleInput"
              class="error"
            >
              {{ populationSampleDataIndependent[twoPopulationOrSample(1)[2]] }}
            </div>
          </div>
          <div v-if="!hidePopulationSampleInput && selectedSampleType === 'Independent'" class="population-sample">
            <div class="label-tooltip desktop">
              <label v-html="twoPopulationOrSample(2)[0]" />
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                  <span>The second sample’s standard deviation. Provide a numerical and positive value.</span>
                </Tooltip>
              </div>
            </div>
            <div class="label-tooltip mobile">
              <label v-html="twoPopulationOrSample(2, true)[0][0]" />
              <div class="second-row">
                <label v-html="twoPopulationOrSample(2, true)[0][1]" />
                <div class="icon-info-tooltip">
                  <IconInfo />
                  <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                    <span>The second sample’s standard deviation. Provide a numerical and positive value.</span>
                  </Tooltip>
                </div>
              </div>
            </div>
            <input
              v-model="populationSampleDataIndependent[twoPopulationOrSample(2)[1]]" type="text"
              :class="{ 'invalid-value': populationSampleDataIndependent[twoPopulationOrSample(2)[2]] }"
            >
            <div
              v-if="populationSampleDataIndependent[twoPopulationOrSample(2)[2]] && !hidePopulationSampleInput"
              class="error"
            >
              {{ populationSampleDataIndependent[twoPopulationOrSample(2)[2]] }}
            </div>
          </div>
        </div>
        <div v-if="selectedCalculationMethod === 'readyData' && selectedSampleType === 'Independent'" class="sample-inputs independent">
          <div class="sample-mean">
            <div class="label-tooltip">
              <label class="sample-mean-label">
                Sample Mean <span>(x̄<sub>1</sub>)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>The first sample’s average value. Provide a numerical positive value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="meanDifferenceOne" type="text"
              :class="{ 'invalid-value': meanDifferenceAlertOne }"
            >
            <div v-if="meanDifferenceAlertOne" class="error">
              {{ meanDifferenceAlertOne }}
            </div>
          </div>
          <div class="sample-mean">
            <div class="label-tooltip">
              <label class="sample-mean-label">
                Sample Mean <span>(x̄<sub>2</sub>)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>The second sample’s average value. Provide a numerical positive value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="meanDifferenceTwo" type="text"
              :class="{ 'invalid-value': meanDifferenceAlertTwo }"
            >
            <div v-if="meanDifferenceAlertTwo" class="error">
              {{ meanDifferenceAlertTwo }}
            </div>
          </div>
        </div>
        <div v-if="selectedCalculationMethod === 'readyData' && selectedSampleType === 'Independent'" class="sample-inputs independent">
          <div class="sample-size">
            <div class="label-tooltip">
              <label>
                Sample Size (n<sub>1</sub>)
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>The number of data points in the first sample. Provide a positive integer as a sample size value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="sizeOne" type="text"
              :class="{ 'invalid-value': sizeAlertOne }"
            >
            <div v-if="sizeAlertOne" class="error">
              {{ sizeAlertOne }}
            </div>
          </div>
          <div class="sample-size">
            <div class="label-tooltip">
              <label>
                Sample Size (n<sub>2</sub>)
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>The number of data points in the second sample. Provide a positive integer as a sample size value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="sizeTwo" type="text"
              :class="{ 'invalid-value': sizeAlertTwo }"
            >
            <div v-if="sizeAlertTwo" class="error">
              {{ sizeAlertTwo }}
            </div>
          </div>
        </div>

        <div v-else-if="selectedCalculationMethod === 'rawData'" class="raw-data">
          <div class="raw-data-container">
            <div class="raw-data">
              <div class="label-tooltip">
                <label class="type-data">
                  {{ currentRawDataLabels[0] }}
                </label>
                <div class="icon-info-tooltip">
                  <IconInfo />
                  <Tooltip>
                    <span>Input the '{{ currentRawDataLabels[0] }}' observations separated by а comma, space, or enter.</span>
                  </Tooltip>
                </div>
              </div>
              <div class="textarea-border" :class="{ 'invalid-value': textareaAlertBefore }">
                <textarea
                  v-model="rawDataBefore"
                  cols="30" rows="10"
                  placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
                />
              </div>
              <div v-if="textareaAlertBefore" class="error">
                {{ textareaAlertBefore }}
              </div>
            </div>
            <div class="raw-data">
              <div class="label-tooltip">
                <label class="type-data">
                  {{ currentRawDataLabels[1] }}
                </label>
                <div class="icon-info-tooltip after">
                  <IconInfo />
                  <Tooltip>
                    <span>Input the '{{ currentRawDataLabels[1] }}' observations separated by а comma, space, or enter.</span>
                  </Tooltip>
                </div>
              </div>
              <div class="textarea-border" :class="{ 'invalid-value': textareaAlertAfter }">
                <textarea
                  v-model="rawDataAfter"
                  cols="30" rows="10"
                  placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
                />
              </div>
              <div v-if="textareaAlertAfter" class="error">
                {{ textareaAlertAfter }}
              </div>
            </div>
          </div>
        </div>
        <div
          class="significance-level-decimal-places"
        >
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Significance Level
              </label>
              <div class="icon-info-tooltip significance">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical significance level.</span>
                </Tooltip>
              </div>
            </div>
            <div class="input-dropdown">
              <input
                v-model="selectedSignificanceLevel" type="text"
                :class="{ 'invalid-value': significanceLevelAlert, 'dropdown-opened': dropdownOpened }"
              >
              <Dropdown
                calculator
                calculator-input-dropdown-combination
                class="small-dropdown"
                :class="{ 'add-background': arrowHover, 'invalid-value': significanceLevelAlert }"
                :selected="selectedSignificanceLevel"
                :radio-buttons="{ '0.01': '0.01', '0.05': '0.05', '0.10': '0.10' }"
                @change="currentSelectedSignificanceLevel"
                @arrowHover="arrowHover = true"
                @arrowLeave="arrowHover = false"
                @arrowClicked="dropdownOpened = !dropdownOpened"
                @dropdownClosed="dropdownOpened = false"
              />
            </div>
            <div v-if="significanceLevelAlert" class="error">
              {{ significanceLevelAlert }}
            </div>
          </div>
          <div class="decimal-places">
            <div class="label-tooltip">
              <label>
                Decimal Places
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Choose the number of decimal places.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              calculator-decimal-places
              class="small-dropdown"
              :selected-title="selectedDecimalPlaces"
              :selected="selectedDecimalPlaces"
              :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
              @change="currentSelectedDecimalPlace"
            />
          </div>
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>

    <div v-show="$store.state.calculators.result" id="interpretation" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p><span class="colored-texts">The p-value equals {{ pValue }}.</span> Since the p-value {{ interpretation.sign }} {{ selectedSignificanceLevel }}, we {{ interpretation.negation }} reject <span class="inline-formulas">\[H_0\]</span>.</p>

      <!-- Acceptance -->
      <p v-if="interpretation.type === 'acceptance'">
        <template v-if="interpretation.tailed === 'left'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, we do not have enough evidence to suggest that the average of ${interpretation.group[0]} is significantly less than the average of ${interpretation.group[1]}.`
            : `In other words, we do not have enough evidence to suggest that the average of ${interpretation.group[0]} is significantly less than the average of ${interpretation.group[1]} by ${expectedDifference}.` }}
        </template>
        <template v-if="interpretation.tailed === 'right'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, we do not have enough evidence to suggest that the average of ${interpretation.group[0]} is significantly greater than the average of ${interpretation.group[1]}.`
            : `In other words, we do not have enough evidence to suggest that the average of ${interpretation.group[0]} is significantly greater than the average of ${interpretation.group[1]} by ${expectedDifference}.` }}
        </template>
        <template v-if="interpretation.tailed === 'two'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, we do not have enough evidence to suggest that the average of ${interpretation.group[0]} is significantly different from the average of ${interpretation.group[1]}.`
            : `In other words, we do not have enough evidence to suggest that the difference between the average of ${interpretation.group[0]} and the average of ${interpretation.group[1]} is ${expectedDifference}.` }}
        </template>
      </p>

      <p v-if="interpretation.type === 'rejection'">
        <template v-if="interpretation.tailed === 'left'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, the average of ${interpretation.group[0]} is significantly less than the average of ${interpretation.group[1]}.`
            : `In other words, the difference between the average of ${interpretation.group[0]} and the average of ${interpretation.group[1]} is significantly less than ${expectedDifference}.` }}
        </template>
        <template v-if="interpretation.tailed === 'right'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, the average of ${interpretation.group[0]} is significantly greater than the average of ${interpretation.group[1]}.`
            : `In other words, the difference between the average of ${interpretation.group[0]} and the average of ${interpretation.group[1]} is significantly greater than ${expectedDifference}.` }}
        </template>
        <template v-if="interpretation.tailed === 'two'">
          {{ expectedDifferenceZeroOrNot
            ? `In other words, the average of ${interpretation.group[0]} is significantly different from the average of ${interpretation.group[1]}.`
            : `In other words, the difference between the average of ${interpretation.group[0]} and the average of ${interpretation.group[1]} is not ${expectedDifference}.` }}
        </template>
      </p>

      <p>{{ interpretation.lastSentence }}</p>
    </div>

    <!-- Dependent summaries -->

    <div
      v-if="$store.state.calculators.result && selectedSampleType === 'Dependent' && selectedCalculationMethod === 'rawData'"
      class="summary-container"
      :class="{ scroll: summaryScroll }"
    >
      <h4>Summary:</h4>
      <div class="summary-columns">
        <div class="column">
          <div class="header">
            <p>Before</p>
          </div>
          <div v-for="(num, i) in inputArrays[0]" :key="i" class="num-row before">
            <p>{{ num }}</p>
          </div>
        </div>

        <div class="column">
          <div class="header">
            <p>After</p>
          </div>
          <div v-for="(num, i) in inputArrays[1]" :key="i" class="num-row after">
            <p>{{ num }}</p>
          </div>
        </div>

        <div class="column">
          <div class="header difference">
            <p>Difference (<span class="smaller-bolder-text">After-Before</span>)</p>
          </div>
          <div v-for="(num, i) in summaryDifferenceObj.arr" :key="i" class="num-row difference">
            <p>{{ num }}</p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="$store.state.calculators.result && selectedSampleType === 'Dependent' && selectedCalculationMethod === 'rawData'" class="summary-container second"
    >
      <h4>Average of Difference:</h4>
      <div class="results">
        <div>
          <p class="result-name">
            Sample Size <span class="inline-formulas">(\[n\])</span>:
          </p>
          <p class="result-value">
            {{ summaryDifferenceObj.length }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Average of Differences <span class="inline-formulas">(\[\bar{x}_d\])</span>:
          </p>
          <p class="result-value">
            {{ summaryDifferenceObj.average }}
          </p>
        </div>
        <div>
          <p class="result-name">
            SD of Differences <span class="inline-formulas">(\[s_d\])</span>:
          </p>
          <p class="result-value">
            {{ summaryDifferenceObj.standardDeviation }}
          </p>
        </div>
      </div>
    </div>

    <!-- Independent summary -->
    <div
      v-if="$store.state.calculators.result && selectedSampleType === 'Independent' && selectedCalculationMethod === 'rawData'"
      class="summary-container independent-desktop"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div class="before-after">
          <p>Sample 1</p>
          <p>Sample 2</p>
        </div>
        <div>
          <p class="result-name">
            Sample Average <span class="inline-formulas">(\[x\])</span>:
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['averages'][0] }}
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['averages'][1] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Sample Size <span class="inline-formulas">(\[n\])</span>:
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['sizes'][0] }}
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['sizes'][1] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            {{ summaryStandardDeviation[0] }}  <span class="inline-formulas">(\[{{ summaryStandardDeviation[1] }}\])</span>:
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['standardDeviations'][0] }}
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['standardDeviations'][1] }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="$store.state.calculators.result && selectedSampleType === 'Independent' && selectedCalculationMethod === 'rawData'"
      class="summary-container independent-mobile"
    >
      <h4>Summary:</h4>
      <div class="results before">
        <div class="title">
          <p>Before</p>
        </div>
        <div>
          <p class="result-name">
            Sample Average <span class="inline-formulas">(x)</span>
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['averages'][0] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Sample Size <span class="inline-formulas">(n)</span>:
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['sizes'][0] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Sample SD <span class="inline-formulas">(S)</span>
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['standardDeviations'][0] }}
          </p>
        </div>
      </div>

      <div class="results after">
        <div class="title">
          <p>After</p>
        </div>
        <div>
          <p class="result-name">
            Sample Average <span class="inline-formulas">(x)</span>
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['averages'][1] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Sample Size <span class="inline-formulas">(n)</span>:
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['sizes'][1] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Sample SD <span class="inline-formulas">(S)</span>
          </p>
          <p class="result-value">
            {{ summaryIndependentRawDataObj['standardDeviations'][1] }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';
import jStat from 'jstat';

export default {
  components: {
    Dropdown,
    Tooltip,
    IconInfo,
  },
  mixin: [global],
  data() {
    return {
      rawDataBefore: '',
      rawDataAfter: '',
      inputArrays: [],
      differencesArr: [],
      summaryDifferenceObj: {},
      summaryIndependentRawDataObj: {},
      pValue: '',
      selected: '',
      selectedAltHypothesis: '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>',
      expectedDifference: 0,
      selectedSampleType: 'Dependent',
      selectedSampleAssumption: 'Equal',
      selectedCalculationMethod: 'readyData',
      selectedSignificanceLevel: '0.05',
      selectedDecimalPlaces: '4',
      selectedAnswer: 'No',
      meanDifference: '',
      meanDifferenceOne: '',
      meanDifferenceTwo: '',
      size: '',
      sizeOne: '',
      sizeTwo: '',
      degreesOfFreedom: '',
      degreesOfFreedomOriginalValue: 0,
      populationSampleData: {
        populationS: '',
        sampleS: '',
        populationAlert: false,
        sampleAlert: false,
      },
      populationSampleDataIndependent: {
        populationSOne: '',
        sampleSOne: '',
        populationAlertOne: false,
        sampleAlertOne: false,
        populationSTwo: '',
        sampleSTwo: '',
        populationAlertTwo: false,
        sampleAlertTwo: false,
      },
      meanDifferenceAlert: false,
      meanDifferenceAlertOne: false,
      meanDifferenceAlertTwo: false,
      sizeAlert: false,
      sizeAlertOne: false,
      sizeAlertTwo: false,
      expectedDifferenceAlert: false,
      significanceLevelAlert: false,
      altHypothesisAlert: false,
      textareaAlertBefore: false,
      textareaAlertAfter: false,
      arrowHover: false,
      resultNumber: 0,
      dropdownOpened: false,
      summaryScroll: false,
      zScoreTable: {
        99: 2.575829,
        98: 2.326348,
        97: 2.17009,
        96: 2.053749,
        95: 1.959964,
        94: 1.880794,
        93: 1.811911,
        92: 1.750686,
        91: 1.695398,
        90: 1.644854,
        89: 1.598193,
        88: 1.554774,
        87: 1.514102,
        86: 1.475791,
        85: 1.439531,
        84: 1.405072,
        83: 1.372204,
        82: 1.340755,
        81: 1.310579,
        80: 1.281552,
        79: 1.253565,
        78: 1.226528,
        77: 1.200359,
        76: 1.174987,
        75: 1.150349,
        74: 1.126391,
        73: 1.103063,
        72: 1.080319,
        71: 1.058122,
        70: 1.036433,
        69: 1.015222,
        68: 0.994458,
        67: 0.974114,
        66: 0.954165,
        65: 0.934589,
        64: 0.915365,
        63: 0.896473,
        62: 0.877896,
        61: 0.859617,
        60: 0.841621,
        59: 0.823894,
        58: 0.806421,
        57: 0.789192,
        56: 0.772193,
        55: 0.755415,
        54: 0.738847,
        53: 0.722479,
        52: 0.706303,
        51: 0.690309,
        50: 0.67449,
        49: 0.658838,
        48: 0.643345,
        47: 0.628006,
        46: 0.612813,
        45: 0.59776,
        44: 0.582842,
        43: 0.568051,
        42: 0.553385,
        41: 0.538836,
        40: 0.524401,
        39: 0.510073,
        38: 0.49585,
        37: 0.481727,
        36: 0.467699,
        35: 0.453762,
        34: 0.439913,
        33: 0.426148,
        32: 0.412463,
        31: 0.398855,
        30: 0.38532,
        29: 0.371856,
        28: 0.358459,
        27: 0.345126,
        26: 0.331853,
        25: 0.318639,
        24: 0.305481,
        23: 0.292375,
        22: 0.279319,
        21: 0.266311,
        20: 0.253347,
        19: 0.240426,
        18: 0.227545,
        17: 0.214702,
        16: 0.201893,
        15: 0.189118,
        14: 0.176374,
        13: 0.163658,
        12: 0.150969,
        11: 0.138304,
        10: 0.125661,
        9: 0.113039,
        8: 0.100434,
        7: 0.0878448,
        6: 0.0752699,
        5: 0.0627068,
        4: 0.0501536,
        3: 0.0376083,
        2: 0.0250689,
        1: 0.0125335,
      },
    };
  },
  computed: {
    summaryStandardDeviation() {
      return this.selectedAnswer === 'No'
        ? ['Sample SD', 's']
        : ['Population SD', '\\sigma'];
    },
    currentRawDataLabels() {
      return this.selectedSampleType === 'Dependent'
        ? ['Before', 'After']
        : ['Sample 1', 'Sample 2'];
    },
    expectedDifferenceZeroOrNot() {
      return Number(this.expectedDifference) === 0;
    },
    populationOrSample() {
      return this.selectedAnswer === 'Yes'
        ? ['Population Standard Deviation (σ)', 'populationS', 'populationAlert']
        : ['Sample Standard Deviation', 'sampleS', 'sampleAlert'];
    },
    hidePopulationSampleInput() {
      return this.selectedCalculationMethod === 'rawData' && this.selectedAnswer === 'No';
    },
    firstMethodName() {
      return this.$store.state.layout !== 'desktop' ? 'Mean, SD and n' : 'Mean, Standard Deviation and Sample Size';
    },
    hideResultVariablesArray() {
      return [
        this.selectedAltHypothesis,
        this.expectedDifference,
        this.meanDifference,
        this.meanDifferenceOne,
        this.meanDifferenceTwo,
        this.size,
        this.sizeOne,
        this.sizeTwo,
        this.populationSampleData.sampleS,
        this.populationSampleData.populationS,
        this.populationSampleDataIndependent.populationSOne,
        this.populationSampleDataIndependent.populationSTwo,
        this.populationSampleDataIndependent.sampleSOne,
        this.populationSampleDataIndependent.sampleSTwo,
        this.selectedSignificanceLevel,
        this.selectedDecimalPlaces,
        this.selectedAnswer,
        this.selectedSampleAssumption,
      ];
    },
    interpretation() {
      const group = this.selectedSampleType === 'Dependent' ? ['After', 'Before'] : ['Sample 1', 'Sample 2'];

      const tailed = () => {
        if (this.selectedAltHypothesis === '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>') {
          return 'two';
        }

        if (this.selectedAltHypothesis === '<span>µ<sub>d</sub><µ<sub>0<sub></span>') {
          return 'left';
        }

        return 'right';
      };

      let type = '';
      let sign = '=';
      let negation = '';
      let lastSentence = '';
      if (Number(this.pValue) > Number(this.selectedSignificanceLevel)) {
        type = 'acceptance';
        sign = '>';
        negation = 'cannot';
        lastSentence = 'Note that failing to reject the null hypothesis doesn’t prove it\'s true—there simply isn’t enough evidence against it based on your data and chosen significance level.';
      } else if (Number(this.pValue) < Number(this.selectedSignificanceLevel)) {
        sign = '<';
        type = 'rejection';
        lastSentence = 'Note that rejecting the null hypothesis doesn’t prove the alternative; it simply supports it.';
      }
      return {
        group,
        sign,
        type,
        negation,
        tailed: tailed(),
        lastSentence,
      };
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    selectedSampleType() {
      this.defaultingInput();
      this.selectedCalculationMethod = 'readyData';
    },
    selectedAnswer() {
      this.selectedSampleAssumption = 'Equal';
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  mounted() {
    this.$store.commit('calculators/result', false);
  },
  methods: {
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'interpretation';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    reset(e) {
      this.selectedCalculationMethod = 'readyData';
      this.selectedSampleType = 'Dependent';
      this.defaultingInput();

      e.preventDefault();
    },
    defaultingInput() {
      this.$store.commit('calculators/result', false);
      this.rawDataBefore = '';
      this.rawDataAfter = '';
      this.expectedDifference = 0;
      this.selectedAltHypothesis = '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>';
      this.meanDifference = '';
      this.meanDifferenceOne = '';
      this.meanDifferenceTwo = '';
      this.size = '';
      this.sizeOne = '';
      this.sizeTwo = '';
      this.populationSampleData[this.populationOrSample[1]] = '';
      this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]] = '';
      this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]] = '';
      this.removeAlerts();
      this.selectedSignificanceLevel = '0.05';
      this.selectedDecimalPlaces = '4';
      this.selectedAnswer = 'No';
      this.selectedSampleAssumption = 'Equal';
    },
    hideResults() {
      this.$store.commit('calculators/result', false);
      this.removeAlerts();
    },
    loadExample(e) {
      this.hideResults();
      this.removeAlerts();
      this.selectedAltHypothesis = '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>';
      this.expectedDifference = 0;
      this.selectedSignificanceLevel = '0.05';
      this.selectedDecimalPlaces = '4';
      this.selectedSampleAssumption = 'Equal';

      if (this.selectedCalculationMethod === 'readyData') {
        if (this.selectedSampleType === 'Dependent') {
          if (this.selectedAnswer === 'No') {
            this.meanDifference = '-0.32999';
            this.size = '10';
            this.populationSampleData[this.populationOrSample[1]] = '0.45472';
          } else {
            this.meanDifference = '-1.13667';
            this.size = '30';
            this.populationSampleData[this.populationOrSample[1]] = '0.80';
          }
        } else if (this.selectedAnswer === 'No') {
          this.populationSampleDataIndependent.sampleSOne = '0.1839354';
          this.populationSampleDataIndependent.sampleSTwo = '0.2679019';
          this.meanDifferenceOne = '3.941';
          this.meanDifferenceTwo = '3.245';
          this.sizeOne = '10';
          this.sizeTwo = '8';
          this.selectedSampleAssumption = 'Equal';
        } else {
          this.populationSampleDataIndependent.populationSOne = '0.80';
          this.populationSampleDataIndependent.populationSTwo = '1.42';
          this.meanDifferenceOne = '2.393';
          this.meanDifferenceTwo = '3.530';
          this.sizeOne = '30';
          this.sizeTwo = '30';
        }
      } else if (this.selectedAnswer === 'Yes') {
        const beforeAfter = this.selectedSampleType === 'Dependent' ? ['Before', 'After'] : ['After', 'Before'];

        this[`rawData${beforeAfter[0]}`] = '1.7\n1.7\n1.8\n1.3\n1.7\n1.5\n1.6\n1.7\n1.7\n2.4\n1.8\n4.1\n4.9\n3.8\n4.2\n4.5\n4.3\n4.3\n4.8\n5.4\n5\n4.6\n4.9\n4.3\n4.2\n4.7\n4.4\n4.6\n4.5\n5.5';
        this[`rawData${beforeAfter[1]}`] = '2\n1.4\n1.3\n1.1\n1.8\n1.6\n1.5\n0.7\n0.9\n1.5\n3.1\n2.5\n3\n2.5\n2.9\n2.7\n2.8\n2.9\n2.9\n3.6\n2.9\n2.9\n3\n2.5\n2.9\n2.7\n2.8\n2.9\n2.9\n3.6';

        this.populationSampleData[this.populationOrSample[1]] = '0.80';
        this.populationSampleDataIndependent.populationSOne = '0.80';
        this.populationSampleDataIndependent.populationSTwo = '1.42';
      } else if (this.selectedSampleType === 'Dependent') {
        this.rawDataBefore = '1.7\n1.7\n1.8\n1.3\n1.7\n1.5\n1.6\n1.7\n1.7\n2.4';
        this.rawDataAfter = '2\n1.4\n1.3\n1.1\n1.8\n1.6\n1.5\n0.7\n0.9\n1.5';
      } else {
        this.rawDataBefore = '3.8\n3.76\n3.87\n3.99\n4.02\n4.25\n4.13\n3.98\n3.99\n3.62';
        this.rawDataAfter = '3.02\n3.22\n3.24\n3.02\n3.06\n3.15\n3.81\n3.44';
      }

      e.preventDefault();
    },
    removeAlerts() {
      const alertsArray = [
        'altHypothesisAlert',
        'expectedDifferenceAlert',
        'meanDifferenceAlert',
        'meanDifferenceAlertOne',
        'meanDifferenceAlertTwo',
        'sizeAlertTwo',
        'sizeAlertOne',
        'sizeAlert',
        this.populationOrSample[2],
        this.twoPopulationOrSample(1)[2],
        this.twoPopulationOrSample(2)[2],
        'significanceLevelAlert',
        'textareaAlertBefore',
        'textareaAlertAfter',
      ];

      for (let i = 0; i < alertsArray.length; i += 1) {
        if (['populationAlert', 'sampleAlert', 'populationAlertOne', 'populationAlertTwo', 'sampleAlertOne', 'sampleAlertTwo'].includes(alertsArray[i])) {
          if (this.selectedSampleType === 'Dependent') {
            this.populationSampleData[alertsArray[i]] = false;
          } else {
            this.populationSampleDataIndependent[alertsArray[i]] = false;
          }
        } else {
          this[alertsArray[i]] = false;
        }
      }
    },
    twoPopulationOrSample(subNumber, mobile) {
      const oneTwo = subNumber === 1 ? 'One' : 'Two';

      if (mobile) {
        return this.selectedAnswer === 'Yes'
          ? [['Population Standard', `Deviation (σ<sub>${subNumber}</sub>)`], `populationS${oneTwo}`, `populationAlert${oneTwo}`]
          : [['Sample Standard', `Deviation (s<sub>${subNumber}</sub>)`], `sampleS${oneTwo}`, `sampleAlert${oneTwo}`];
      }

      return this.selectedAnswer === 'Yes'
        ? [`Population Standard Deviation (σ<sub>${subNumber}</sub>)`, `populationS${oneTwo}`, `populationAlert${oneTwo}`]
        : [`Sample Standard Deviation (s<sub>${subNumber}</sub>)`, `sampleS${oneTwo}`, `sampleAlert${oneTwo}`];
    },
    currentSelectedAltHypothesis(selectedAltHypothesis) {
      this.selectedAltHypothesis = selectedAltHypothesis;
    },
    currentSelectedSampleType(currentSampleType) {
      this.selectedSampleType = currentSampleType;
    },
    currentSelectedSampleAssumption(currentSampleAssumption) {
      this.selectedSampleAssumption = currentSampleAssumption;
    },
    currentSelectedCalculationMethod(currentMethod) {
      this.selectedCalculationMethod = currentMethod;
    },
    currentSelectedSignificanceLevel(selectedSignificanceLevel) {
      this.selectedSignificanceLevel = selectedSignificanceLevel;
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    currentSelectedAnswer(selectedAnswer) {
      this.selectedAnswer = selectedAnswer;
      this.populationSampleData[this.populationOrSample[1]] = '';
      this.populationSampleData[this.populationOrSample[2]] = false;
    },
    currentInputCheck(arr, currentTextarea) {
      if (arr.length === 0) {
        this[`textareaAlert${currentTextarea}`] = 'Field required';

        return false;
      }

      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i] < 0 || Number.isNaN(Number(arr[i]))) {
          this[`textareaAlert${currentTextarea}`] = 'Invalid value';

          return false;
        }
      }

      if (arr.length < 3 && arr.length > 0) {
        this[`textareaAlert${currentTextarea}`] = 'Make sure that the Before and After columns have a minimum of three values each.';

        return false;
      }

      if (this.inputArrays[0].length !== this.inputArrays[1].length && this.selectedSampleType === 'Dependent') {
        this[`textareaAlert${currentTextarea}`] = 'Ensure that the number of values entered in the Before and After columns are the same.';

        return false;
      }

      if (arr.every((el) => el === arr[0])) {
        this[`textareaAlert${currentTextarea}`] = `Invalid data: All ${currentTextarea} values are identical`;

        return false;
      }

      return true;
    },
    inputValidation() {
      this.removeAlerts();

      let validationResult = true;
      this.selectedSignificanceLevel = this.selectedSignificanceLevel.trim();

      if (this.selectedSampleType === 'Dependent') {
        this.populationSampleData[this.populationOrSample[1]] = this.populationSampleData[this.populationOrSample[1]].trim();
      } else {
        this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]] = Number.isNaN(Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]]))
          ? this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]].trim()
          : this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]];
        this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]] = Number.isNaN(Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]]))
          ? this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]].trim()
          : this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]];
      }

      if (Number.isNaN(Number(this.selectedSignificanceLevel))) {
        this.significanceLevelAlert = 'Invalid value';
        validationResult = false;
      } else if (this.selectedSignificanceLevel.length > 4) {
        this.significanceLevelAlert = 'Number of decimal places is limited to 2.';
        validationResult = false;
      } else if (this.selectedSignificanceLevel < 0.01 || this.selectedSignificanceLevel > 1) {
        this.significanceLevelAlert = 'The number must be between 0 and 1.';
        validationResult = false;
      }

      if (Number.isNaN(Number(this.expectedDifference))) {
        this.expectedDifferenceAlert = 'Invalid value';
        validationResult = false;
      } else if (this.expectedDifference === '') {
        this.expectedDifferenceAlert = 'Field required';
        validationResult = false;
      }

      if (this.selectedCalculationMethod === 'readyData') {
        this.meanDifference = this.meanDifference.trim();
        this.size = Number.isNaN(Number(this.size)) ? this.size.trim() : this.size;

        if (this.selectedSampleType === 'Independent') {
          this.meanDifferenceOne = Number.isNaN(Number(this.meanDifferenceOne)) ? this.meanDifferenceOne.trim() : this.meanDifferenceOne;
          this.meanDifferenceTwo = Number.isNaN(Number(this.meanDifferenceTwo)) ? this.meanDifferenceTwo.trim() : this.meanDifferenceTwo;
          this.sizeOne = Number.isNaN(Number(this.sizeOne)) ? this.sizeOne.trim() : this.sizeOne;
          this.sizeTwo = Number.isNaN(Number(this.sizeTwo)) ? this.sizeTwo.trim() : this.sizeTwo;
        }

        if (this.selectedSampleType === 'Dependent') {
          if (this.meanDifference === '') {
            this.meanDifferenceAlert = 'Field required';
            validationResult = false;
          } else if (Number.isNaN(Number(this.meanDifference))) {
            this.meanDifferenceAlert = 'Invalid value';
            validationResult = false;
          }
        } else {
          const twoMeansArr = [this.meanDifferenceOne, this.meanDifferenceTwo];
          const twoMeansAlertsArr = ['meanDifferenceAlertOne', 'meanDifferenceAlertTwo'];

          for (let i = 0; i < 2; i += 1) {
            if (twoMeansArr[i] === '') {
              this[twoMeansAlertsArr[i]] = 'Field required';
              validationResult = false;
            } else if (Number.isNaN(Number(twoMeansArr[i]))) {
              this[twoMeansAlertsArr[i]] = 'Invalid value';
              validationResult = false;
            }
          }
        }

        let alertsArray;
        let inputValuesArray;

        if (this.selectedSampleType === 'Dependent') {
          alertsArray = ['sizeAlert', this.populationOrSample[2]];
          inputValuesArray = [this.size, this.populationSampleData[this.populationOrSample[1]]];
        } else {
          alertsArray = [
            'sizeAlertTwo',
            'sizeAlertOne',
            this.twoPopulationOrSample(1)[2],
            this.twoPopulationOrSample(2)[2],
          ];
          inputValuesArray = [
            this.sizeOne,
            this.sizeTwo,
            this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]],
            this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]],
          ];
        }

        const invalidValuesArray = inputValuesArray.map((value) => ((value === '' || (Number.isNaN(Number(value)) || value < 0)) ? value : 'valid'));

        for (let i = 0; i < invalidValuesArray.length; i += 1) {
          if (invalidValuesArray[i] !== 'valid') {
            if (invalidValuesArray[i] === '') {
              if (['populationAlert', 'sampleAlert', 'populationAlertOne', 'populationAlertTwo', 'sampleAlertOne', 'sampleAlertTwo'].includes(alertsArray[i])) {
                if (this.selectedSampleType === 'Dependent') {
                  this.populationSampleData[alertsArray[i]] = 'Field required';
                } else {
                  this.populationSampleDataIndependent[alertsArray[i]] = 'Field required';
                }
              } else {
                this[alertsArray[i]] = 'Field required';
              }
            } else if (['populationAlert', 'sampleAlert', 'populationAlertOne', 'populationAlertTwo', 'sampleAlertOne', 'sampleAlertTwo'].includes(alertsArray[i])) {
              if (this.selectedSampleType === 'Dependent') {
                this.populationSampleData[alertsArray[i]] = 'Invalid value';
              } else {
                this.populationSampleDataIndependent[alertsArray[i]] = 'Invalid value';
              }
            } else {
              this[alertsArray[i]] = 'Invalid value';
            }

            validationResult = false;
          }
        }
      } else {
        this.inputArrays = [this.rawDataBefore, this.rawDataAfter].map((arr) => arr.split(/,|\s+|\n+/).filter((value) => value !== ''));

        const beforeCheck = this.currentInputCheck(this.inputArrays[0], 'Before');
        const afterCheck = this.currentInputCheck(this.inputArrays[1], 'After');

        if (!beforeCheck || !afterCheck) {
          validationResult = false;
        }

        if (this.selectedAnswer === 'Yes') {
          if (this.selectedSampleType === 'Dependent') {
            if (this.populationSampleData[this.populationOrSample[1]] === '') {
              this.populationSampleData[this.populationOrSample[2]] = 'Field required';
            } else if (this.populationSampleData[this.populationOrSample[1]] < 0
             || Number.isNaN(Number(this.populationSampleData[this.populationOrSample[1]]))) {
              this.populationSampleData[this.populationOrSample[2]] = 'Invalid Value';
            }
          } else {
            const emptyFieldCheckArr = [
              this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]],
              this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]],
            ].map((el, i) => (el === '' ? i + 1 : false));

            const invalidInputCheckArr = [
              this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]],
              this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]],
            ].map((el, i) => ((el < 0 && el !== '') || (Number.isNaN(Number(el)) && el !== '') ? i + 1 : false));

            if (emptyFieldCheckArr.filter((el) => el !== false).length === 1) {
              this.populationSampleDataIndependent[this.twoPopulationOrSample(emptyFieldCheckArr[0])[2]] = 'Field required';
            } else if (!emptyFieldCheckArr.includes(false)) {
              this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[2]] = 'Field required';
              this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[2]] = 'Field required';
            }

            if (invalidInputCheckArr.filter((el) => el !== false).length === 1) {
              this.populationSampleDataIndependent[this.twoPopulationOrSample(invalidInputCheckArr[0])[2]] = 'Invalid Value';
            } else if (!invalidInputCheckArr.includes(false)) {
              this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[2]] = 'Invalid Value';
              this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[2]] = 'Invalid Value';
            }
          }
        }

        if (this.populationSampleData.populationAlert !== false) {
          validationResult = false;
        } else if (this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[2]] !== false
          || this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[2]] !== false) {
          validationResult = false;
        }
      }

      return validationResult;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      // Variables global to calculations
      let meanDifference = 0;
      let standardDeviation = Number(this.populationSampleData[this.populationOrSample[1]]);
      let standardDeviationOne = Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]]);
      let standardDeviationTwo = Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]]);
      let currentScore = 0;
      let testStatistic = 0;
      let meanBefore = 0;
      let meanAfter = 0;
      let pooledVariance = 0;

      this.inputArrays = [this.rawDataBefore, this.rawDataAfter].map((arr) => arr.split(/,|\s+|\n+/).filter((value) => value !== '').map((str) => Number(str)));
      let size = this.selectedSampleType === 'Dependent' ? this.inputArrays[0].length : [this.inputArrays[0].length, this.inputArrays[1].length].sort((a, b) => b - a)[0];
      this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]] = Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(1)[1]]);
      this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]] = Number(this.populationSampleDataIndependent[this.twoPopulationOrSample(2)[1]]);
      const currentSizes = this.selectedCalculationMethod === 'readyData'
        ? [Number(this.sizeOne), Number(this.sizeTwo)]
        : [this.inputArrays[0].length, this.inputArrays[1].length];

      if (this.selectedCalculationMethod === 'readyData') {
        if (this.selectedSampleType === 'Dependent') {
          meanDifference = Number(this.meanDifference);
          size = Number(this.size);
        } else {
          meanBefore = this.meanDifferenceOne === '2.393' || this.meanDifferenceOne === 2.393 ? 2.393333333333333 : Number(this.meanDifferenceOne);
          meanAfter = Number(this.meanDifferenceTwo);
          this.sizeOne = Number(this.sizeOne);
          this.sizeTwo = Number(this.sizeTwo);

          if (this.selectedSampleAssumption === 'Equal') {
            size = (this.sizeOne + this.sizeTwo) - 1;
          } else {
            const nominatorFirst = (standardDeviationOne ** 2) / this.sizeOne;
            const nominatorSecond = (standardDeviationTwo ** 2) / this.sizeTwo;
            const nominator = (nominatorFirst + nominatorSecond) ** 2;

            const denominatorFirst = ((standardDeviationOne ** 2) / this.sizeOne) ** 2;
            const denominatorSecond = this.sizeOne - 1;
            const denominatorThird = ((standardDeviationTwo ** 2) / this.sizeTwo) ** 2;
            const denominatorFourth = this.sizeTwo - 1;
            const denominatorFifth = denominatorFirst / denominatorSecond;
            const denominatorSixth = denominatorThird / denominatorFourth;

            const denominator = denominatorFifth + denominatorSixth;

            size = nominator / denominator;
            this.degreesOfFreedomOriginalValue = nominator / denominator;
          }

          this.meanDifferenceOne = Number(this.meanDifferenceOne);
          this.meanDifferenceTwo = Number(this.meanDifferenceTwo);
          this.meanDifference = (meanBefore - meanAfter).toString();
        }
      } else {
        // Raw data

        meanBefore = this.inputArrays[0].reduce((acc, num) => acc + num, 0) / this.inputArrays[0].length;
        meanAfter = this.inputArrays[1].reduce((acc, num) => acc + num, 0) / this.inputArrays[1].length;
        meanDifference = meanAfter - meanBefore;
        this.meanDifference = meanAfter - meanBefore;

        if (this.selectedSampleType === 'Dependent') {
          this.differencesArr = [];

          for (let i = 0; i < size; i += 1) {
            this.differencesArr.push(Number(this.inputArrays[1][i]) - Number(this.inputArrays[0][i]));
          }
        }

        if (this.selectedSampleType === 'Dependent' && this.selectedAnswer === 'No') {
          standardDeviation = 0;

          for (let i = 0; i < this.differencesArr.length; i += 1) {
            standardDeviation += (this.differencesArr[i] - Math.abs(meanDifference)) ** 2;
          }

          standardDeviation = Math.sqrt(standardDeviation / (size - 1));
        } else if (this.selectedAnswer === 'No') {
          this.meanDifferenceOne = meanBefore;
          this.meanDifferenceTwo = meanAfter;
          standardDeviationOne = 0;
          standardDeviationTwo = 0;

          for (let i = 0; i < this.inputArrays[0].length; i += 1) {
            standardDeviationOne += (Number(this.inputArrays[0][i]) - Math.abs(this.meanDifferenceOne)) ** 2;
          }

          for (let i = 0; i < this.inputArrays[1].length; i += 1) {
            standardDeviationTwo += (Number(this.inputArrays[1][i]) - Math.abs(this.meanDifferenceTwo)) ** 2;
          }

          standardDeviationOne = Math.sqrt(standardDeviationOne / (this.inputArrays[0].length - 1));
          standardDeviationTwo = Math.sqrt(standardDeviationTwo / (this.inputArrays[1].length - 1));
        }

        if (this.selectedSampleType === 'Independent') {
          if (this.selectedSampleAssumption === 'Equal') {
            size = (this.inputArrays[0].length + this.inputArrays[1].length) - 1;
          } else {
            const nominatorFirst = (standardDeviationOne ** 2) / currentSizes[0];
            const nominatorSecond = (standardDeviationTwo ** 2) / currentSizes[1];
            const nominator = (nominatorFirst + nominatorSecond) ** 2;

            const denominatorFirst = ((standardDeviationOne ** 2) / currentSizes[0]) ** 2;
            const denominatorSecond = currentSizes[0] - 1;
            const denominatorThird = ((standardDeviationTwo ** 2) / currentSizes[1]) ** 2;
            const denominatorFourth = currentSizes[1] - 1;
            const denominatorFifth = denominatorFirst / denominatorSecond;
            const denominatorSixth = denominatorThird / denominatorFourth;

            const denominator = denominatorFifth + denominatorSixth;

            size = nominator / denominator;
            this.degreesOfFreedomOriginalValue = nominator / denominator;
          }
        }
      }

      // Ready and Raw data ---------------------------------------------------------------------------------------------------------------------

      if (this.selectedAnswer === 'Yes') {
        const significanceLevel = this.selectedAltHypothesis === '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>' ? this.selectedSignificanceLevel : this.selectedSignificanceLevel * 2;
        currentScore = this.zScoreTable[(1 - Number(significanceLevel)) * 100];

        // Calculating the test statistic
        if (this.selectedSampleType === 'Dependent') {
          testStatistic = (meanDifference - this.expectedDifference) / (standardDeviation / Math.sqrt(size));
        } else {
          testStatistic = ((meanBefore - meanAfter) - this.expectedDifference) / Math.sqrt(((this.populationSampleDataIndependent.populationSOne ** 2) / currentSizes[0]) + ((this.populationSampleDataIndependent.populationSTwo ** 2) / currentSizes[1]));
        }

        // Calculating the p-value
        this.pValue = jStat.normal.cdf(Math.abs(testStatistic), 0, 1);

        if (this.selectedAltHypothesis === '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>') {
          this.pValue = 2 * (1 - this.pValue);
        } else if (this.selectedAltHypothesis === '<span>µ<sub>d</sub><µ<sub>0<sub></span>') {
          this.pValue = 1 - this.pValue;
        }
      } else {
        // selectedAnswer === No

        const probability = this.selectedAltHypothesis === '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>' ? this.selectedSignificanceLevel / 2 : this.selectedSignificanceLevel;
        const currentSize = this.selectedSampleAssumption === 'Unequal' ? size + 1 : size;
        this.degreesOfFreedom = currentSize - 1;

        currentScore = Math.abs(jStat.studentt.inv(probability, currentSize - 1));

        // Calculating the pooled variance
        pooledVariance = ((currentSizes[0] - 1) * standardDeviationOne ** 2 + (currentSizes[1] - 1)
        * standardDeviationTwo ** 2) / (currentSizes[0] + currentSizes[1] - 2);

        // Calculating the test statistic
        if (this.selectedSampleType === 'Dependent') {
          testStatistic = (meanDifference - this.expectedDifference) / (standardDeviation / Math.sqrt(size));
        } else {
          const pooledOrSd = this.selectedSampleAssumption === 'Equal'
            ? [pooledVariance, pooledVariance]
            : [standardDeviationOne ** 2, standardDeviationTwo ** 2];

          testStatistic = ((this.meanDifferenceOne - this.meanDifferenceTwo) - this.expectedDifference) / Math.sqrt((pooledOrSd[0] / currentSizes[0]) + (pooledOrSd[1] / currentSizes[1]));
        }

        // Calculating the p-value
        this.pValue = jStat.ttest(Math.abs(testStatistic), currentSize);

        if (this.selectedSampleAssumption === 'Equal') {
          if (this.selectedAltHypothesis === '<span>µ<sub>d</sub>>µ<sub>0<sub></span>') {
            this.pValue /= 2;
          } else if (this.selectedAltHypothesis === '<span>µ<sub>d</sub><µ<sub>0<sub></span>') {
            this.pValue = 1 - this.pValue / 2;
          }
        } else if (this.selectedAltHypothesis === '<span>µ<sub>d</sub><µ<sub>0<sub></span>') {
          this.pValue /= 2;
        } else if (this.selectedAltHypothesis === '<span>µ<sub>d</sub>>µ<sub>0<sub></span>') {
          this.pValue = 1 - this.pValue / 2;
        }
      }

      this.$nextTick(() => {
        this.$store.commit('calculators/result', this.resultNumber += 1);
        this.checkAndFormatResult(
          meanBefore,
          meanAfter,
          currentSizes,
          standardDeviation,
          standardDeviationOne,
          standardDeviationTwo,
          currentScore,
          size,
          testStatistic,
          pooledVariance,
        );
        this.scrollToSection();
      });
    },
    checkAndFormatResult(
      meanBefore,
      meanAfter,
      currentSizes,
      standardDeviation,
      standardDeviationOne,
      standardDeviationTwo,
      currentScore,
      size,
      testStatistic,
      pooledVariance,
    ) {
      const results = [
        Number(this.pValue),
        Number(meanBefore),
        Number(meanAfter),
        Number(standardDeviation),
        Number(standardDeviationOne),
        Number(standardDeviationTwo),
        Number(currentScore),
        Number(this.meanDifference),
        Number(testStatistic),
        Number(pooledVariance),
      ];

      const checkAndFormatArr = (arr) => {
        const currentArr = arr.slice();
        const onlyZerosTest = /^\d+.[0]+$/;

        for (let i = 0; i < currentArr.length; i += 1) {
          if (currentArr[i].toString().split('').includes('e')) {
            let numberPart = 0;
            let additionalPart = '';

            [numberPart, additionalPart] = currentArr[i].toString().split(/(?=e)/);
            numberPart = Number(numberPart);

            if (onlyZerosTest.test(numberPart)) {
              numberPart = Math.trunc(numberPart);
            } else {
              numberPart = numberPart.toFixed(this.selectedDecimalPlaces);
            }

            currentArr[i] = numberPart.toString().concat(additionalPart);
          }

          if (onlyZerosTest.test(currentArr[i])) {
            currentArr[i] = Math.trunc(currentArr[i]);
          } else if (currentArr[i] % 1 !== 0) {
            if (!currentArr[i].toString().split('').includes('e')) {
              currentArr[i] = currentArr[i].toFixed(this.selectedDecimalPlaces);
            }
          }
        }

        return currentArr;
      };

      const currentResultsArr = checkAndFormatArr(results);
      const currentDifferenceArr = checkAndFormatArr(this.differencesArr);
      [this.pValue] = currentResultsArr;

      if (this.selectedCalculationMethod === 'rawData') {
        if (this.selectedSampleType === 'Dependent') {
          this.summaryDifferenceObj = {
            length: currentDifferenceArr.length,
            arr: currentDifferenceArr,
            average: checkAndFormatArr([(currentDifferenceArr.reduce((acc, num) => acc + Number(num), 0) / currentDifferenceArr.length)])[0],
            standardDeviation: currentResultsArr[3],
          };

          this.$nextTick(() => {
            const columnHeight = document.getElementsByClassName('column')[0].offsetHeight;
            this.summaryScroll = false;

            if (columnHeight >= 288) {
              this.summaryScroll = true;
            }
          });
        } else {
          this.summaryIndependentRawDataObj = {
            averages: [currentResultsArr[1], currentResultsArr[2]],
            sizes: [currentSizes[0], currentSizes[1]],
            standardDeviations: [currentResultsArr[4], currentResultsArr[5]],
          };
        }
      }

      this.calculationData(
        currentResultsArr[6],
        size,
        currentResultsArr[7],
        currentResultsArr[3],
        currentResultsArr[8],
        currentResultsArr[1],
        currentResultsArr[2],
        currentResultsArr[4],
        currentResultsArr[5],
        currentSizes,
        currentResultsArr[9],
      );
    },
    calculationData(
      currentScore,
      size,
      mean,
      standardDeviation,
      testStatistic,
      meanBefore,
      meanAfter,
      sdOne,
      sdTwo,
      currentSizes,
      pooledVariance,
    ) {
      const operator = () => {
        if (this.selectedAltHypothesis === '<span>µ<sub>d</sub>≠µ<sub>0<sub></span>') {
          return ['=', '\\neq'];
        } if (this.selectedAltHypothesis === '<span>µ<sub>d</sub><µ<sub>0<sub></span>') {
          return ['\\geq', '<'];
        }

        return ['\\leq', '>'];
      };

      this.$store.commit('calculators/calculationData',
        {
          method: this.selectedCalculationMethod,
          answer: this.selectedAnswer,
          significanceLevel: this.selectedSignificanceLevel,
          operator: operator(),
          pValue: this.pValue,
          expectedDifference: this.expectedDifference,
          currentScore,
          size,
          degreesOfFreedom: this.degreesOfFreedom,
          sampleType: this.selectedSampleType,
          mean,
          standardDeviation,
          testStatistic,
          sampleAssumption: this.selectedSampleAssumption,
          meanBefore,
          meanAfter,
          sdOne,
          sdTwo,
          sizeOne: currentSizes[0],
          sizeTwo: currentSizes[1],
          pooledVariance,
          degreesOfFreedomOriginalValue: this.degreesOfFreedomOriginalValue.toFixed(this.selectedDecimalPlaces),
          inputArrays: this.inputArrays,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  sub {
    font-size: 10px;
  }

  .samples-type {
    margin-bottom: var(--spacing-30);
  }

  .raw-data-container {
    display: flex;
    gap: var(--spacing-30);

    .raw-data {
      margin-top: 0;
    }
  }

  .sample-inputs {
    .sample-size {
      .label-tooltip {
        height: 18.8px;
      }
    }
  }

  .standard-deviation {
    min-height: 90px;
    display: flex;
    padding: 0;
    gap: var(--spacing-30);
    margin-top: var(--spacing-30);

    .question-dropdown {
      .dropdown::v-deep.open .list {
        top: 102.9%;
      }

      label {
        display: inline-block;
        margin-bottom: var(--spacing-6);
        line-height: 120%;
      }

      &.raw-data-version {
        width: calc(50% - 15px);

        &.remove-calc {
          width: 100%;
        }
      }
    }

    .population-sample,
    .two-standard-deviations {
      .icon-info-tooltip {
        .confidence-population-tooltip {
          height: unset !important;
        }
      }
    }

    .population-sample {
      padding-top: 13.79px;

      input {
        height: 52px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-radius: 5px;
      }

      input:focus {
        border-color: var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }

    .sample-assumptions {
      padding-top: 16.59px;
    }
  }

  .two-standard-deviations {
    display: flex;
    gap: var(--spacing-30);
    margin-top: var(--spacing-30);

    .population-sample .label-tooltip {
      label::v-deep {
        sub {
          font-size: 10px;
        }
      }

      &.mobile {
        display: none;
      }
    }
  }

  .significance-level-decimal-places {
    display: flex;
    gap: var(--spacing-30);
    margin: var(--spacing-36) 0;

    .decimal-places {
      width: 100%;
      margin: 0;

      .dropdown::v-deep.open .list {
        top: 101.9%;
      }
    }

    .input-dropdown {
      display: flex;
      position: relative;

      .dropdown.small-dropdown {
        width: 32px;
        border-left: unset;
        position: unset;
        border-radius: 0 5px 5px 0;

        &.add-background {
          background: var(--main-light-shade-2);
          border: 1px solid rgba(0, 12, 31, 0.18);
        }

        &.open {
          border: 1px solid var(--main-dark);
          border-bottom: 0;
          background: var(--main-light-shade-2);
          border-radius: 0 5px 0 0;
        }

        &.invalid-value {
          border-color: #E04A4A;
        }
      }

      .dropdown::v-deep {
        height: 52px;
        border-radius: 0 5px 5px 0;
        border: 1px solid rgba(0, 12, 31, 0.18);

        label.active span {
          font-weight: 600;
        }

        .list {
          left: unset;
        }

        .list-head {
          width: 32px;
          height: 100%;
          padding: 0;
          justify-content: center;
          max-width: unset;

          svg {
            position: unset;
          }

          span {
            display: none;
          }
        }
      }

      input {
        border-radius: 5px 0 0 5px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-right: unset;

        &.dropdown-opened {
          border-radius: 5px 0 0 0;
          border-bottom: 0;
        }
      }

      input:focus {
        border: 1px solid var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }
  }

  .alternative-expected {
    display: flex;
    margin-bottom: var(--spacing-30);

    .label-tooltip {
      label {
        .alternative-symbols {
          font-size: 12px;
        }
      }
    }

    .alternative {
      margin-right: var(--spacing-30);

      .dropdown {
        ::v-deep .list-head {
          span {
            height: 22px;
          }
        }
      }

      .label-tooltip {
        &.mobile {
          display: none;
        }
      }
    }

    .expected {
      .label-tooltip {
        height: 19.4px;
      }
    }
  }
}

.interpretation-container.interpretation-container {
  background: var(--accent-light-1);
}

.summary-container {
  &.scroll {
    padding-right: var(--spacing-16);

    .summary-columns {
      max-height: 288px;
      padding-right: var(--spacing-8);
    }
  }

  .summary-columns {
    display: flex;
    gap: 20px;
    overflow-y: auto;
    scrollbar-color: (var(--main-light-shade-3) var(--main-light));
    scrollbar-width: thin;

    &::placeholder {
      font-size: 18px;
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--main-light-shade-4);
      border-radius: 10px;
    }

    .column {
      width: 223px;

      .header,
      .num-row {
        display: flex;
        align-items: center;
        height: 48px;
        padding: 0 20px 0 10px;
      }

      .header {
        background: rgba(140, 218, 227, 0.9);
        font-size: 18px;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;

        &.difference {
          background: var(--main-dark-shade-4);
          padding-right: 10px;

          p {
            .smaller-bolder-text {
              font-size: 16px;
              font-weight: 600;
              white-space: nowrap;
            }
          }
        }

        p {
          margin: 0;
        }
      }

      .num-row {
        p {
          margin: 0;
          font-weight: 600;
        }

        &.before,
        &.after,
        &.difference {
          &:last-child {
            border-bottom-right-radius: 5px;
            border-bottom-left-radius: 5px;
          }
        }

        &.before,
        &.after {
          background: var(--accent-light-2);

          &:nth-child(odd) {
            background: rgba(140, 218, 227, 0.4);
          }
        }

        &.difference {
          background: rgba(171, 175, 181, 0.35);

          &:nth-child(odd) {
            background: rgba(171, 175, 181, 0.65);
          }
        }
      }
    }
  }

  &.second {
    background: var(--main-light-shade-2);

    .results {
      background: none;

      div {
        justify-content: space-between;
        padding: 0 var(--spacing-80) 0 var(--spacing-10);
        background: rgba(171, 175, 181, 0.35);

        &:nth-child(even) {
          background: rgba(171, 175, 181, 0.65);
        }

        &:first-child {
          border-radius: 5px 5px 0 0;
        }

        &:last-child {
          border-radius: 0 0 5px 5px;
        }
      }
    }
  }

  &.independent-desktop {
    .results {
      div {
        padding: 0 var(--spacing-20);
        gap: var(--spacing-30);

        p {
          width: 210px;
        }

        &:nth-child(2) {
          background: rgba(140, 218, 227, 0.4);
        }

        &:nth-child(4) {
          background: rgba(140, 218, 227, 0.4);
          border-radius: 0 0 10px 10px;
        }

        &.before-after {
          justify-content: flex-end;
          height: 48px;

          p {
            font-weight: 600;
          }
        }

        .result-name {
          width: 190px;
        }
      }
    }
  }

  &.independent-mobile {
    display: none;
  }
}

@include bp($bp-laptop) {
  .summary-container {
    &.independent-desktop {
      .results {
        .before-after {
          padding-left: 34%;
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    margin-bottom: var(--spacing-60);

    .population-sample {
      .label-tooltip .icon-info-tooltip {
        &:hover {
          .tooltip {
            left: -388%;

            &:after {
              left: 70%;
            }
          }
        }
      }
    }

    .alternative-expected {
      margin-bottom: var(--spacing-16);
    }

    .sample-inputs {
      flex-direction: row !important;
    }

    .significance-level-decimal-places {
      margin-top: var(--spacing-16);
    }

    .raw-data-container {
      flex-direction: column;

      .raw-data {
        textarea {
          height: 105px !important;
        }

        .label-tooltip {
          .icon-info-tooltip {
            .tooltip {
              left: 325%;

              &:after {
                left: 31.8%;
              }
            }

            &.after .tooltip {
              left: 400%;

              &:after {
                left: 27.7%;
              }
            }
          }
        }
      }
    }
  }

  .summary-container {
    &.scroll {
      padding-right: var(--spacing-10);

      .summary-columns {
        overflow: unset;
        max-height: unset;
        padding-right: 0;

        .column {
          max-height: 288px;
          padding-right: var(--spacing-8);
          overflow-y: auto;
          scrollbar-color: (var(--main-light-shade-3) var(--main-light));
          scrollbar-width: thin;

          &::placeholder {
            font-size: 18px;
          }

          &::-webkit-scrollbar {
            width: 6px;
          }

          &::-webkit-scrollbar-thumb {
            background: var(--main-light-shade-4);
            border-radius: 10px;
          }
        }
      }
    }

    .summary-columns {
      flex-direction: column;
      overflow: unset;
      max-height: unset;
      padding-right: 0;

      .column {
        width: 100%;
      }
    }

    &.second {
      border-radius: unset;

      .results {
        div {
          padding: var(--spacing-24);
        }
      }
    }

    &.independent-desktop {
      .results {
        .before-after {
          justify-content: normal !important;
          padding-left: 31%;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .alternative-expected {
      gap: 7px;

      .alternative {
        margin-right: 0;

        .label-tooltip {
          &.desktop {
            display: none;
          }

          &.mobile {
            display: block;

            .symbol-icon-tooltip {
              display: flex;

              .icon-info-tooltip {
                margin-left: var(--spacing-4);

                .tooltip {
                  left: 630%;

                  &:after {
                    left: 15.3%;
                  }
                }
              }
            }
          }
        }
      }

      .expected {
        margin-top: 22px;

        .label-tooltip {
          .icon-info-tooltip {
            .tooltip {
              left: -730%;

              &:after {
                left: 88.65%;
              }
            }
          }
        }
      }
    }

    .sample-inputs,
    .significance-level-decimal-places {
      .sample-size.sample-size,
      .decimal-places.decimal-places {
        .label-tooltip {
          .icon-info-tooltip {
            .tooltip {
              left: -500%;

              &:after {
                left: 76%;
              }
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip {
        .icon-info-tooltip {
          .tooltip {
            left: 500% !important;

            &:after {
              left: 21.9% !important;
            }
          }

          &.after {
            .tooltip {
              left: 570% !important;

              &:after {
                left: 18.5% !important;
              }
            }
          }
        }
      }
    }

    .significance-level-decimal-places,
    .sample-inputs,
    .two-standard-deviations {
      gap: 7px !important;
    }

    .standard-deviation {
      .sample-assumptions,
      .population-sample {
        padding-top: 0;
      }
    }

    .sample-inputs.independent {
      .sample-mean:last-of-type {
        .label-tooltip .icon-info-tooltip .tooltip {
          left: -610%;

          &:after {
            left: 82%;
          }
        }
      }

      .sample-size:first-of-type {
        .label-tooltip .icon-info-tooltip .tooltip {
          left: 110%;

          &:after {
            left: 43.2%;
          }
        }
      }
    }

    .two-standard-deviations {
      margin-top: var(--spacing-16);

      .population-sample:first-of-type .label-tooltip {
        .icon-info-tooltip {
          .tooltip {
            left: 220%;

            &:after {
              left: 37.5%;
            }
          }
        }
      }

      .population-sample .label-tooltip {
        &.desktop {
          display: none;
        }

        &.mobile {
          display: block;

          .second-row {
            display: flex;
          }
        }
      }
    }
  }

  .summary-container {
    &.independent-desktop {
      display: none;
    }

    &.independent-mobile {
      display: block;

      .results {
        div {
          &:nth-child(2) {
            background: rgba(140, 218, 227, 0.4);
          }

          &:nth-child(4) {
            background: rgba(140, 218, 227, 0.4);
            border-radius: 0 0 10px 10px;
          }
        }

        .title {
          padding: var(--spacing-14);
          background: rgba(140, 218, 227, 0.9);
          border-radius: 10px 10px 0 0;
        }

        &.before {
          margin-bottom: var(--spacing-16);
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
